import { useEffect } from 'react'
import * as Yup from 'yup'
import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import styled from '@emotion/styled'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { Button, Box, Stack } from '@mui/material'
import { useMutation, useQuery } from '@tanstack/react-query'
import { ChainProtocol, OperationType, PayoutToken, READABLE_PROTOCOL, Token } from '@archax/shared-types'
import { getAvailableEligibilityTokensByProtocol, getAvailablePayoutTokensByProtocol } from '../../../api/payouts'
import onApiError from '../../../util/on-api-error'
import { createOperationForPayoutContract } from '../../../api/operations'
import { DISABLED_CHAINS } from '../../../constants'
import TextField from '../../../components/ui/TextField/TextField'
import { SelectWithForm } from '../../../components/ui/Select/Select'
import { DateTimePickerWithForm } from '../../../components/ui/DateTimePicker'
import { StyledCircularProgress } from '../../../components/ui/styled-components'

const Wrapper = styled('div')(() => ({
  width: '100%',
  minWidth: '42rem',
}))

const tokenProtocolOptions = [{ label: READABLE_PROTOCOL[ChainProtocol.Hedera], value: ChainProtocol.Hedera }].filter(
  (it) => !DISABLED_CHAINS.includes(it.value),
)

type PayoutTokenFormData = {
  name: string
  protocol: string
  startAt: Date
  endAt: Date
  eligibilityTokenId: string
  payoutToken: PayoutToken
}

type CreatePayoutFormProps = {
  onCancel: () => void
  onSuccess: () => void
}
export default function CreatePayoutForm({ onCancel, onSuccess }: CreatePayoutFormProps) {
  const { t } = useTranslation()

  const validationSchema = Yup.object()
    .shape({
      protocol: Yup.string().required(),
      name: Yup.string().required(),
      startAt: Yup.string()
        .test('is-before', 'The start date must be before end date', (value, ctx) =>
          !!ctx.parent.endAt && value ? dayjs(ctx.parent.endAt).isAfter(dayjs(value)) : true,
        )
        .required(),
      endAt: Yup.string()
        .test('is-after', 'The end date must be after start date', (value, ctx) =>
          !!ctx.parent.endAt && value ? dayjs(ctx.parent.startAt).isBefore(dayjs(value)) : true,
        )
        .required(),
      eligibilityTokenId: Yup.string().required(),
      payoutToken: Yup.object().required(),
    })
    .required()

  const initialValues = {
    protocol: ChainProtocol.Hedera,
    name: '',
    eligibilityTokenId: '',
    payoutToken: undefined,
  }
  const { mutateAsync } = useMutation(
    (formData: PayoutTokenFormData) => createOperationForPayoutContract(OperationType.PayoutContractCreate, formData),
    {
      onSuccess: (data) => {
        toast.success(t('payoutContracts.createNewContract.successMessage'))
        onSuccess()
      },
      onError: onApiError,
    },
  )

  const formMethods = useForm<PayoutTokenFormData>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: initialValues,
    resolver: yupResolver(validationSchema),
  })
  const {
    formState: { isValid, isSubmitting },
  } = formMethods

  const { data: availableEligibilityTokens, refetch: refetchAvailableEligibilityTokens } = useQuery(
    [`get-available-eligibility-tokens-${formMethods.watch('protocol')}`],
    () =>
      (async () => {
        if (formMethods.watch('protocol')) {
          const response = await getAvailableEligibilityTokensByProtocol(formMethods.watch('protocol') as ChainProtocol)
          return response.data?.data
        }
        return []
      })(),
    {
      retry: false,
      onError: onApiError,
      cacheTime: 0,
    },
  )

  const { data: availablePayoutTokens, refetch: refetchAvailablePayoutTokens } = useQuery(
    [`get-available-payout-tokens-${formMethods.watch('protocol')}`],
    () =>
      (async () => {
        if (formMethods.watch('protocol')) {
          const response = await getAvailablePayoutTokensByProtocol(formMethods.watch('protocol') as ChainProtocol)
          return response.data
        }
        return []
      })(),
    {
      retry: false,
      onError: onApiError,
      cacheTime: 0,
    },
  )

  const onSubmit = async (data: PayoutTokenFormData) => {
    await mutateAsync({
      ...data,
      startAt: dayjs(data.startAt).utc(true).toISOString(),
      endAt: dayjs(data.endAt).utc(true).toISOString(),
    } as unknown as PayoutTokenFormData)
  }

  useEffect(() => {
    refetchAvailableEligibilityTokens()
    refetchAvailablePayoutTokens()
  }, [formMethods.watch('protocol')])

  useEffect(() => {
    if (availablePayoutTokens?.length) {
      formMethods.setValue('payoutToken', availablePayoutTokens[0])
    }
  }, [availablePayoutTokens])

  return (
    <Wrapper>
      <FormProvider {...formMethods}>
        <form onSubmit={formMethods.handleSubmit(async (data) => !isSubmitting && (await onSubmit(data)))}>
          <Box mb={4}>
            <SelectWithForm
              fullWidth
              id="protocol"
              name="protocol"
              label={t('payoutContracts.createNewContract.labels.protocol')}
              options={tokenProtocolOptions}
            />
          </Box>
          <Box mb={4}>
            <TextField
              name="name"
              control={formMethods.control}
              label={t('payoutContracts.createNewContract.labels.name')}
              style={{ marginBottom: 0 }}
            />
          </Box>
          <Box mb={4}>
            <DateTimePickerWithForm
              name="startAt"
              label={t('payoutContracts.createNewContract.labels.startAt')}
              slotProps={{ textField: { variant: 'standard', fullWidth: true, style: { marginBottom: 4 } } }}
            />
          </Box>
          <Box mb={4}>
            <DateTimePickerWithForm
              name="endAt"
              label={t('payoutContracts.createNewContract.labels.endAt')}
              slotProps={{ textField: { variant: 'standard', fullWidth: true, style: { marginBottom: 4 } } }}
            />
          </Box>
          <Box mb={4}>
            <SelectWithForm
              fullWidth
              id="eligibility-token-id"
              name="eligibilityTokenId"
              label={t('payoutContracts.createNewContract.labels.eligibilityToken')}
              options={(availableEligibilityTokens || []).map((it: Token) => ({
                value: it.id,
                label: it.name,
              }))}
            />
          </Box>
          <Box mb={4}>
            <SelectWithForm
              fullWidth
              optKey="address"
              id="payout-token"
              name="payoutToken"
              label={t('payoutContracts.createNewContract.labels.payoutToken')}
              options={(availablePayoutTokens || []).map((it: PayoutToken) => ({
                value: it,
                label: it.name,
              }))}
              disabled
            />
          </Box>
          <Stack flex={'row'} flexDirection={'row'} gap="15px">
            <Button size="large" variant="outlined" fullWidth onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" size="large" color="primary" fullWidth disabled={!isValid}>
              {isSubmitting && <StyledCircularProgress size={'1.2rem'} />}
              {isSubmitting ? 'Submitting' : 'Submit'}
            </Button>
          </Stack>
        </form>
      </FormProvider>
    </Wrapper>
  )
}
